import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import "../../FrontOffice/OutPatients/OutpatientHome.css"

import {
    AppointmentCard,
    CommonGridToolBarWithFilterText,
    CommonPatientDetails,
} from "../../../Components/Common Components/CommonComponents";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { Colors } from "../../../Styles/Colors";
import { AmountFormat, ContentInTooltip, formatDate } from "../../../Components/CommonFunctions/CommonFunctions";
import { ImagePaths } from "../../../Utility/ImagePaths";
import { renderIconButton } from "../../Doctor/DoctorNotes/Common/DocCommonComponent";
import PaymentsIcon from '@mui/icons-material/Payments';
import ReportIcon from '@mui/icons-material/Report';
import CollectAdvanceModal from "./CounsellorAdvanceCollect";

const CounsellorWalkIn = (props) => {
    // paging & UI
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // data
    const [appointments, setAppointments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [statusCount, setStatusCount] = useState({});
    const [menuRow, setMenuRow] = useState(null);
    const [openAdvance, setOpenAdvance] = useState(false);

    // filters / controls
    const [filterDate, setFilterDate] = useState(new Date());
    const [selectedStatus, setSelectedStatus] = useState(""); // "" => All
    const [searchKey, setSearchKey] = useState("");
    const [printLoading, setPrintLoading] = useState(false);
    const [errorMsg, seterrorMsg] = useState(false);
    const [errorMsgText, seterrorMsgText] = useState('');
    const [successMsg, setsuccessMsg] = useState(false);
    const [successMsgText, setsuccessMsgText] = useState('');
    const pageSizeOptions = [10, 20, 30];

    const errorMessage = (msg) => {
        seterrorMsgText(msg)
        seterrorMsg(true)
        setIsLoading(false)
    }

    const successMessage = (msg) => {
        setsuccessMsgText(msg)
        setsuccessMsg(true)
        setIsLoading(false)
    }

    const msgClose = () => {
        seterrorMsg(false);
        seterrorMsgText('');
        setsuccessMsg(false);
        setsuccessMsgText('');
    }

    useEffect(() => {
        fetchAppointments();
    }, [selectedStatus, filterDate, searchKey]);

    // Fetch appointments using AYURVEDA_COUNSELLOR endpoint
    const fetchAppointments = useCallback(() => {
        setIsLoading(true);
        try {
            const dateKey = filterDate
                ? dayjs(filterDate).format("YYYY-MM-DD")
                : "";
            const qs = `?status=${selectedStatus || ""}&date=${dateKey}&search=${encodeURIComponent(
                searchKey || ""
            )}`;
            RestAPIService.getAll(Serviceurls.AYURVEDA_COUNSELLOR_WALKIN + qs)
                .then((res) => {
                    if (res.data.status === "success") {
                        // convert rows and add temp_start_date for datepicker
                        const list = (res.data.data || []).map((row, idx) => ({
                            ...row,
                            sno: idx + 1,
                        }));
                        setAppointments(list);
                        setStatusCount(res.data || {});
                    } else {
                        errorMessage(res.data.message || "Failed to fetch appointments");
                    }
                })
                .catch((e) => errorMessage(e?.response?.data?.message || e.message))
                .finally(() => setIsLoading(false));
        } catch (e) {
            setIsLoading(false);
            errorMessage(e.message);
        }
    }, [filterDate, selectedStatus, searchKey]);

    // top card click
    const onCardClick = (statusKey) => {
        setSelectedStatus(statusKey);
    };

    const handleTablePrint = () => {
        const dateKey = filterDate ? dayjs(filterDate).format("YYYY-MM-DD") : "";
        const qs = `?status=${selectedStatus || ""}&date=${dateKey}&doctor_filter=&search=${encodeURIComponent(searchKey || "")}&export_type=pdf`;
        setPrintLoading(true);
        RestAPIService.getAll(Serviceurls.AYURVEDA_COUNSELLOR_WALKIN + qs)
            .then((response) => {
                const file = new Blob([response.data], { type: "application/pdf" });
                window.open(URL.createObjectURL(file));
            })
            .catch((e) => errorMessage(e?.response?.data?.message || e?.message || "Print failed"))
            .finally(() => setPrintLoading(false));
    };

    const PaymentStatus = (orderAmount, advanceAmount, BalanceDetails = {}) => {
        const balAmount = orderAmount - advanceAmount;
        const renderPayment = (Icon, label, amount, color, onClick) => {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} onClick={onClick}>
                    <Icon sx={{ fontSize: "0.9vw", marginRight: '0.4vw', color: color }} />
                    <Typography color={color} fontSize={"0.8vw"}>{`${label} : ${AmountFormat(amount)} `}</Typography>
                </Box>
            )
        }
        return (
            <Box>
                {renderPayment(PaymentsIcon, 'Order', orderAmount, '#616161')}
                {renderPayment(CheckCircleIcon, 'Advance', advanceAmount, 'green')}
                {renderPayment(ReportIcon, 'Balance', balAmount, '#B8170D')}
            </Box>
        )
    }

    const onApproveClick = (row) => {
        setOpenAdvance(true);
        setMenuRow(row)
    }

    // columns (centered headers, widths tuned to screenshot)
    const columns = [
        {
            field: "patient_names",
            headerName: "Patient's Details",
            flex: 0.25,
            renderCell: (params) => (
                <Box>
                    <CommonPatientDetails data={params.row} />
                </Box>
            ),
            headerAlign: "left",
            align: "left",
        },
         {
            field: "doctor_name",
            headerName: "Cosultant Doctor",
            flex: 0.2,
            renderCell: (params) => (
                <Typography sx={{ fontSize: "0.9vw" }}>
                    {params.row?.doctor_name || "-"}
                </Typography>
            ),
            headerAlign: "left",
            align: "left",
        },
        {
            field: "start_date",
            headerName: "Therapy Start Date",
            flex: 0.150,
            renderCell: (params) => (
                <Typography sx={{ fontSize: "0.9vw" }}>
                    {params.row?.start_date ? params.row?.start_date : "-"}
                </Typography>
            ),
            headerAlign: "left",
            align: "left",
        },
        {
            field: "no_of_days",
            headerName: "Days Completed",
            flex: 0.150,
            renderCell: (params) => (
                <Typography sx={{ fontSize: "0.9vw" }}>
                    {(params.row?.completed_days || "0") + " / " + params.row?.no_of_days || "0"}
                </Typography>
            ),
            headerAlign: "left",
            align: "left",
        },
        {
            field: 'order_amount', headerName: 'PaymentStatus',
            headerClassName: 'eMed_Apt_TblHead',
            flex: 0.3, density: 'compact',
            headerAlign: "left", align: "left",
            renderCell: (params) => (
                <div>{PaymentStatus(params.row?.order_amount ? params.row?.order_amount : 0, params.row?.total_advance_amount ? params.row?.total_advance_amount : 0)}</div>)
        },
        // {
        //     field: "action",
        //     headerName: "Action",
        //     flex: 0.15,
        //     headerAlign: "center",
        //     align: "center",
        //     renderCell: (params) => {
        //         {
        //             renderIconButton(
        //                 "Recieve Payment",
        //                 ImagePaths.TickEnable.default,
        //                 () => {
        //                     onApproveClick(params.row);
        //                 },
        //                 false,
        //                 "1.6vw"
        //             )
        //         }
        //     }
        // }
    ];

    return (
        <Box sx={{ height: "85vh", width: "100%", backgroundColor: Colors.Background, p: 1 }}>
            {/* Header area: date picker & cards */}
            <div className="eMed_OP_AptHead" style={{ justifyContent: "space-between" }}>
                <Typography variant="h6" ml={2} fontWeight={500}>WalkIn Patients</Typography>
                <div className='eMed_Header_OP_home_div' style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={filterDate ? filterDate : null}
                            name={"datePicker"}
                            inputFormat='DD-MM-YYYY'
                            views={["year", "month", "day"]}
                            onChange={(newDate) => {
                                setFilterDate(newDate);
                            }}
                            renderInput={(params) => <TextField
                                {...params}
                                label={"Date"}
                                variant="outlined"
                                onKeyDown={(e) => e.preventDefault()}
                                sx={{ width: '15dvw', marginLeft: '1vw' }}
                                size='small'
                            />}
                        />
                    </LocalizationProvider>
                    <Tooltip title="Print" placement="top" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleTablePrint}
                                disabled={!appointments?.length || printLoading}
                            >
                                <PrintIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            </div>
            <Box display="flex" justifyContent="end" alignItems="center">
                <Box display="flex" gap={1}>
                    <AppointmentCard
                        onClick={() => onCardClick("")}
                        count={statusCount?.total_count || 0}
                        label={"All"}
                        color={"#04B7B1"}
                        isSelectedCard={selectedStatus === "" || selectedStatus === null}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick("Booked")}
                        count={statusCount?.total_booked_count || 0}
                        label={"Booked"}
                        color={"#616161"}
                        isSelectedCard={selectedStatus === "Booked"}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick("inprogress")}
                        count={statusCount?.total_inprogress_count || 0}
                        label={"inprogress"}
                        color={"#0461B7"}
                        isSelectedCard={selectedStatus === "inprogress"}
                    />
                    <AppointmentCard
                        onClick={() => onCardClick("Completed")}
                        count={statusCount?.total_completed_count || 0}
                        label={"Completed"}
                        color={"#B8170D"}
                        isSelectedCard={selectedStatus === "Completed"}
                    />
                </Box>
            </Box>

            {/* Data Grid area */}
            <Box sx={{ height: '64vh', backgroundColor: 'white' }}>
                <DataGrid
                    rows={appointments || []}
                    columns={columns}
                    getRowId={(row) => row.id}
                    pageSizeOptions={pageSizeOptions}
                    pagination
                    paginationMode="client"
                    paginationModel={{
                        page: page,
                        pageSize: pageSize
                    }}
                    onPaginationModelChange={(model) => {
                        setPage(model.page)
                        setPageSize(model.pageSize)
                    }}
                    density="comfortable"
                    headerHeight={40}
                    rowHeight={45}
                    disableRowSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            minHeight: 35,
                            maxHeight: 35,
                        },
                    }}

                    components={{
                        Toolbar: () => (
                            <Box sx={{ p: 0.5 }}>
                                <CommonGridToolBarWithFilterText />
                            </Box>
                        ),
                        NoRowsOverlay: () => (
                            <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
                                No Records To Be Shown
                            </Box>
                        ),
                    }}
                />
            </Box>
            <CollectAdvanceModal
                open={menuRow && openAdvance}
                onClose={() => { setOpenAdvance(false); setMenuRow(); }}
                onCollect={(data) => advanceCollect(data)}
                patientData={menuRow}
                errorMessage={(msg) => { errorMessage(msg) }}
            />
            {errorMsg ?
                <ToastMsg
                    severity={'error'}
                    msg={errorMsgText}
                    msgPop={msgClose.bind(this)}
                />
                : null}
            {successMsg ?
                <ToastMsg
                    severity={'success'}
                    msg={successMsgText}
                    msgPop={msgClose.bind(this)}
                />
                : null}
            {isLoading && <Loader />}
        </Box>
    );
};

export default CounsellorWalkIn;